import { attributes, htmlAttributes } from '../../core/ids-attributes';
import { customElement, scss } from '../../core/ids-decorators';
import { stripHTML } from '../../utils/ids-xss-utils/ids-xss-utils';
import { breakpoints, isWidthBelow } from '../../utils/ids-breakpoint-utils/ids-breakpoint-utils';

import IdsTriggerField from '../ids-trigger-field/ids-trigger-field';

import '../ids-trigger-field/ids-trigger-button';
import { IdsInputTemplateVariables } from '../ids-input/ids-input';
import '../ids-icon/ids-icon';
import '../ids-menu-button/ids-menu-button';
import '../ids-popup-menu/ids-popup-menu';

import styles from './ids-search-field.scss';
import { stringToBool } from '../../utils/ids-string-utils/ids-string-utils';
import IdsText from '../ids-text/ids-text';

const DEFAULT_LABEL = 'Search';
const DEFAULT_PLACEHOLDER = 'Type to search';

/**
 * IDS Search Field Component
 * @type {IdsSearchField}
 * @inherits IdsTriggerField
 */
@customElement('ids-search-field')
@scss(styles)
export default class IdsSearchField extends IdsTriggerField {
  constructor() {
    super();
  }

  state: any = {
    categories: [],
    expandedFromTruncated: false
  };

  isFormComponent = true;

  /**
   * Inherited from `IdsColorVariantMixin`
   * @returns {Array<string>} List of available color variants for this component
   */
  colorVariants = ['alternate', 'app-menu', 'card', 'module-nav', 'dropdown'];

  /**
   * Inherited from `IdsColorVariantMixin`. If the Color Variant on Search Fields are changed,
   * switch trigger buttons to the "alternate" style instead of an `app-menu` style.
   * @param {string} variantName the new color variant being applied to the Search Field
   */
  onColorVariantRefresh(variantName?: string) {
    let btnVariantName = variantName || '';
    if (variantName === 'app-menu') {
      btnVariantName = 'alternate';
    }
    const adjustBtnVariant = (btn: any) => {
      btn.setAttribute(attributes.COLOR_VARIANT, btnVariantName);
    };

    this.buttons.forEach(adjustBtnVariant);
    if (this.fieldContainer) {
      [...this.fieldContainer.querySelectorAll('ids-trigger-button')].forEach(adjustBtnVariant);
    }
  }

  static get attributes() {
    return [
      ...super.attributes,
      attributes.CATEGORY,
      attributes.TRUNCATED,
      attributes.KEEP_OPEN_WITH_VALUE,
    ];
  }

  connectedCallback() {
    super.connectedCallback();
    this.#initializeCollapsible();
    this.#attachEventHandlers();
    this.#attachKeyboardListener();

    if (!this.placeholder) {
      this.placeholder = DEFAULT_PLACEHOLDER;
    }
    if (!this.label) {
      this.label = DEFAULT_LABEL;
    }

    // Set up locale change handler for RTL support
    this.onLocaleChange = () => {
      this.#updatePopupAlignment();
    };
  }

  template(): string {
    const {
      ariaLabel,
      containerClass,
      inputClass,
      inputState,
      labelHtml,
      placeholder,
      type,
      value
    } = this.templateVariables();

    const isRTL = this.localeAPI?.isRTL();
    const hostClassList = Array.from(this?.classList || []).join(' ');
    const popupAlign = isRTL ? 'bottom, right' : 'bottom, left';

    return `<div id="ids-search-field" class="ids-search-field ${hostClassList} ids-trigger-field ${containerClass}" part="container">
      ${labelHtml}
      <div class="fieldset">
        <div class="field-container${this.collapsible ? ' collapsible' : ''}${this.collapsed ? ' collapsed' : ''}${this.truncated ? ' truncated' : ''}" part="field-container">
          ${this.categories?.length ? '' : this.searchIcon}
          <slot name="trigger-start"></slot>
          ${this.templateCategoriesMenu()}
          ${this.truncated ? `
            <div class="truncated-wrapper" part="truncated" tabindex="0">
              ${this.categories?.length ? '' : this.searchIcon}
              <ids-text overflow="ellipsis" tooltip="${this.value || ''}">${this.value}</ids-text>
            </div>
          ` : ''}
          <input
            part="input"
            id="${this.id}-input"
            ${type}${inputClass}${placeholder}${inputState}
            ${ariaLabel}
            ${value}
            autocomplete="off"
            ></input>
          <slot name="trigger-end"></slot>
        </div>
        ${this.templateCategoriesButton()}
      </div>
      ${this.autocomplete ? `
        <ids-popup
          type="menu"
          align="${popupAlign}"
          align-target="#${this.id}-input"
          part="popup"
        >
          <ids-list-box slot="content"></ids-list-box>
        </ids-popup>` : ''}
    </div>`;
  }

  templateCategoriesButton(): string {
    if (!this.action) return ``;

    return `
      <ids-button id="category-action-button" appearance="secondary" no-margins>
        <span>${this.action}</span>
      </ids-button>
    `;
  }

  templateCategoriesMenu(): string {
    if (!this.categories?.length) return ``;

    const menuButtonText = this.category
      ? `<span>${this.category}</span>`
      : `<span class="audible">Select Search Category</span>`;

    const isRTL = this.localeAPI?.isRTL();
    const menuAlign = isRTL ? 'bottom, left' : 'bottom, right';

    return `
      <ids-menu-button id="menu-button" appearance="tertiary" menu="category-menu" icon="search" dropdown-icon>
        ${menuButtonText}
      </ids-menu-button>
      <ids-popup-menu id="category-menu" target="menu-button" trigger-type="click" align="${menuAlign}">
        <ids-menu-group select="${this.multiple ? 'multiple' : 'single'}" keep-open="true">
          ${this.categories.map((category, idx) => `<ids-menu-item value="${idx}">${category}</ids-menu-item>`).join('')}
        </ids-menu-group>
      </ids-popup-menu>
    `;
  }

  /**
   * Overrides the standard "focus" behavior to handle collapsible search fields
   */
  focus(): void {
    if (this.collapsible && this.collapsed) {
      this.expandField();
    } else if (this.truncated) {
      this.expandTruncated();
    } else {
      super.focus();
    }
  }

  /**
   * Expand the search field
   */
  expandField() {
    if (!this.collapsible) return;
    this.removeAttribute(attributes.COLLAPSED);
    this.input?.focus();
    this.#updateFieldContainerClass();
    if (this.input) {
      this.input.focus();
      this.input.style.caretColor = 'var(--ids-search-field-color-border-focus)';
    }
    this.triggerEvent('expand', this);
  }

  /**
   * Expand the truncated search field
   */
  expandTruncated() {
    if (!this.truncated) return;
    this.state.expandedFromTruncated = true;
    this.#updateTruncatedText();
    this.removeAttribute(attributes.TRUNCATED);
    if (this.input) {
      this.input.focus();
      // Start cursor at beginning to show left part of text during transition
      this.input.setSelectionRange(0, 0);
      // Move cursor to end after transition for normal editing behavior
      // requestAnimationFrame waits for CSS transition to complete before repositioning cursor
      requestAnimationFrame(() => {
        const length = this.input?.value?.length || 0;
        this.input?.setSelectionRange(length, length);
      });
    }
    this.#updateTruncatedClass();
    this.triggerEvent('expand', this);
  }

  /**
   * Collapse the truncated search field
   * @param {any} event the original click event
   */
  collapseTruncated(event: any) {
    if (!this.state.expandedFromTruncated) return;

    if (this.keepOpenWithValue && this.input?.value?.trim()) return;

    if (
      event.target !== this.input
      && event.target !== this.truncatedWrapper
    ) {
      this.state.expandedFromTruncated = false;
      this.triggerEvent('collapse', this);
      this.setAttribute(attributes.TRUNCATED, '');
      this.#updateTruncatedClass();
    }
  }

  /**
   * Collapse the search field
   * @param {any} event the original click event
   */
  collapseField(event: any) {
    if (!this.collapsible && !this.collapsed) return;

    if (this.keepOpenWithValue && this.input?.value?.trim()) return;
    // Ignore clicks on the clear button
    if (
      this.clearable
      && event.target === this
      && event.type === 'click'
    ) return;

    if (
      event.target !== this.input
      && event.target !== this.expandButton
    ) {
      this.triggerEvent('collapse', this);
      this.setAttribute(attributes.COLLAPSED, '');
      this.#updateFieldContainerClass();
    }
  }

  /**
   * Set focus on the input
   */
  collapsibleFocus() {
    if (!this.collapsible) return;
    if (!this.collapsed) {
      this.input?.focus();
    }
  }

  /**
   * Disable collapsible
   */
  disableCollapsible() {
    this.removeAttribute(attributes.COLLAPSIBLE);
    this.removeAttribute(attributes.COLLAPSED);
    this.#updateFieldContainerClass();
  }

  /**
   * Enable collapsible
   */
  enableCollapsible() {
    this.setAttribute(attributes.COLLAPSIBLE, '');
    this.setAttribute(attributes.COLLAPSED, '');
    this.#updateFieldContainerClass();
  }

  /**
   * Initialize collapsible
   */
  #initializeCollapsible() {
    if (!this.collapsible && !this.collapsibleResponsive) return;
    const size = this.collapsibleResponsive;

    if (
      this.collapsible
      || (size && window.innerWidth <= parseInt(`${breakpoints[size]}`))
    ) {
      this.enableCollapsible();
    } else {
      this.disableCollapsible();
    }
  }

  /**
   * Update the field container classes
   */
  #updateFieldContainerClass() {
    const fieldContainer = this.shadowRoot?.querySelector('.field-container');
    const fieldset = this.shadowRoot?.querySelector('.fieldset');
    const searchField = this.shadowRoot?.querySelector('.ids-search-field');
    if (!fieldContainer) return;

    if (!this.collapsed) {
      fieldContainer.classList.remove('collapsed');
      fieldContainer.setAttribute('tabindex', '0');
      fieldset?.setAttribute('tabindex', '0');
      searchField?.setAttribute('tabindex', '0');
      this.input?.setAttribute('tabindex', '0');
      // Add focus state during expansion
      fieldContainer.classList.add('expanding');
      setTimeout(() => {
        fieldContainer.classList.remove('expanding');
      }, 300);
    } else {
      fieldContainer.classList.add('collapsed');
      fieldContainer.setAttribute('tabindex', '-1');
      fieldset?.setAttribute('tabindex', '-1');
      searchField?.setAttribute('tabindex', '-1');
      this.input?.setAttribute('tabindex', '-1');
    }
  }

  /**
   * Update the truncated classes
   */
  #updateTruncatedClass() {
    const fieldContainer = this.shadowRoot?.querySelector('.field-container');
    if (!fieldContainer) return;

    if (this.truncated) {
      fieldContainer.classList.add('truncated');
      this.input?.setAttribute(attributes.TABINDEX, '-1');
      this.#updateTruncatedText();
    } else {
      fieldContainer.classList.remove('truncated');
      this.input?.setAttribute(attributes.TABINDEX, '0');
      this.input?.focus();
    }
  }

  /**
   * Update the truncated wrapper text
   */
  #updateTruncatedText() {
    const truncatedText = this.shadowRoot?.querySelector<IdsText>('.truncated-wrapper ids-text');
    if (truncatedText) {
      const value = this.input?.value ?? '';
      truncatedText.textContent = value;
      truncatedText.setAttribute(attributes.TOOLTIP, value);
    }
  }

  /**
   * Listener for collapsible-responsive change
   */
  #collapsibleResponsiveChange() {
    if (!this.collapsibleResponsive) return;
    const size = this.collapsibleResponsive;
    const mq = isWidthBelow(size);

    mq.addEventListener('change', () => {
      if (mq.matches) {
        this.enableCollapsible();
      } else {
        this.disableCollapsible();
      }
    });
  }

  /**
   * Get the truncated wrapper
   * @readonly
   * @returns {any} the truncated wrapper
   */
  get truncatedWrapper(): any {
    return this.shadowRoot?.querySelector('.truncated-wrapper');
  }

  /**
   * Get the expand button
   * @readonly
   * @returns {any} the expand button
   */
  get expandButton(): any {
    if (!this.collapsible && !this.collapsibleResponsive) return;
    const btn = this.querySelector('ids-trigger-button[slot="trigger-start"]');
    btn?.classList.add('expand-button');
    btn?.setAttribute('tabindex', '0');
    return btn;
  }

  /**
   * Get the search icon
   * @readonly
   * @returns {any} the search icon
   */
  get searchIcon(): any {
    return `<ids-icon class="ids-icon search-icon starting-icon" size="medium" icon="search"></ids-icon>`;
  }

  #updateCategoriesMenu() {
    const category = this.category;
    const menuButton = this.#categoriesMenuButton;
    if (!category || !menuButton) return;

    const selectedCategories = this.selectedCategories;

    if (selectedCategories.length === 1) {
      menuButton.text = selectedCategories[0];
    } else if (selectedCategories.length > 1) {
      menuButton.text = `${selectedCategories.length} ${this.localeAPI?.translate?.('Selected')}`;
    } else {
      menuButton.text = category;
    }

    const popup = this.#categoriesPopup?.popup;
    const popupWidth = parseInt(popup?.style?.getProperty?.('min-width') ?? 155);
    const popupMinWidth = this.#categoriesMenuButton?.getBoundingClientRect().width ?? popupWidth;
    popup?.style?.setProperty?.('min-width', `${Math.max(popupMinWidth, popupWidth)}px`);

    if (!this.multiple) {
      this.#categoriesPopup?.hide?.();
    }
  }

  get #categoriesPopup(): any {
    return this.shadowRoot?.querySelector('ids-popup-menu');
  }

  get #categoriesMenuButton(): any {
    return this.shadowRoot?.querySelector('ids-menu-button');
  }

  get #categoriesActionButton(): any {
    return this.shadowRoot?.querySelector('ids-button#category-action-button');
  }

  /**
   * Override templateVariables to handle aria-label properly
   * @returns {IdsInputTemplateVariables} template variables including proper aria-label
   */
  templateVariables(): IdsInputTemplateVariables {
    const vars = super.templateVariables();
    // Use aria-label for better screen reader support
    if (this.getAttribute(htmlAttributes.ARIA_LABEL)) {
      vars.ariaLabel = `aria-label="${stripHTML(this.getAttribute(htmlAttributes.ARIA_LABEL) ?? '')}"`;
    }

    return vars;
  }

  /**
   * @returns {string[]} returns an array of all the categories that appear in the category dropdown
   */
  get categories(): string[] { return this.state.categories; }

  /**
   * @param {string[]} value = sets a list of categories that should appear in the category dropdown
   */
  set categories(value: string[]) {
    this.state.categories = value;
    this.#rerender();
  }

  /**
   * @param {string} value - sets the initial text that appears in the categories dropdown menu
   */
  set category(value: string) {
    if (value) {
      this.setAttribute(attributes.CATEGORY, value);
    } else {
      this.removeAttribute(attributes.CATEGORY);
    }
    this.#rerender();
  }

  /**
   * @returns {string} - gets the initial text that appears in the categories dropdown menu
   */
  get category(): string {
    return this.getAttribute(attributes.CATEGORY) ?? '';
  }

  /**
   *  @param {boolean} value - sets if the search field should be collapsible
   */
  set collapsible(value: boolean) {
    if (value) {
      this.setAttribute(attributes.COLLAPSIBLE, '');
    } else {
      this.removeAttribute(attributes.COLLAPSIBLE);
    }
  }

  /**
   * @returns {boolean | string} - gets if the search field should be collapsible
   */
  get collapsible(): boolean {
    return this.hasAttribute(attributes.COLLAPSIBLE);
  }

  /**
   * @param {string} value - sets if the search field should be collapsible
   * based on screen size. Valid values: 'xxl', 'xl', 'lg', 'md', 'sm', 'xs'
   */
  set collapsibleResponsive(value: string) {
    const validBreakpoints = Object.keys(breakpoints);
    const normalizedValue = value.toLowerCase();

    if (validBreakpoints.includes(normalizedValue)) {
      this.setAttribute(attributes.COLLAPSIBLE_RESPONSIVE, value);
    } else {
      this.removeAttribute(attributes.COLLAPSIBLE_RESPONSIVE);
    }
  }

  /**
   * @returns {string | null} - gets the value of the collapsibleResponsive attribute
   */
  get collapsibleResponsive(): string {
    return this.getAttribute(attributes.COLLAPSIBLE_RESPONSIVE) || '';
  }

  /**
   * @param {boolean} value - sets if the search field is collapsed
   */
  set collapsed(value: boolean) {
    if (value) {
      this.setAttribute(attributes.COLLAPSED, '');
    } else {
      this.removeAttribute(attributes.COLLAPSED);
    }
  }

  /**
   * @returns {boolean} - gets if the search field is collapsed
   */
  get collapsed(): boolean {
    return this.hasAttribute(attributes.COLLAPSED);
  }

  set truncated(value: boolean) {
    const val = stringToBool(value);
    if (val) {
      this.setAttribute(attributes.TRUNCATED, '');
    } else {
      this.removeAttribute(attributes.TRUNCATED);
    }
  }

  get truncated(): boolean {
    return this.hasAttribute(attributes.TRUNCATED);
  }

  /**
   * @param {boolean} value - When true, keeps the search field open/expanded when it contains a value
   */
  set keepOpenWithValue(value: boolean) {
    const val = stringToBool(value);
    if (val) {
      this.setAttribute(attributes.KEEP_OPEN_WITH_VALUE, '');
    } else {
      this.removeAttribute(attributes.KEEP_OPEN_WITH_VALUE);
    }
  }

  /**
   * @returns {boolean} - When true, keeps the search field open/expanded when it contains a value
   */
  get keepOpenWithValue(): boolean {
    return this.hasAttribute(attributes.KEEP_OPEN_WITH_VALUE);
  }

  /**
   * @returns {string[]} returns an array of the currently selected categories
   */
  get selectedCategories(): string[] {
    const categories = this.categories;
    const selectedValues = this.#categoriesPopup?.getSelectedValues?.() ?? [];
    const selectedCategories = selectedValues.map((categoryKey: number) => categories[categoryKey]);
    return selectedCategories;
  }

  /**
   * @param {string} value - sets title on the search field action-button
   */
  set action(value: string) {
    if (value) {
      this.setAttribute(attributes.ACTION, value);
    } else {
      this.removeAttribute(attributes.ACTION);
    }
    this.#rerender();
  }

  /**
   * @returns {string} - gets the title that appears on the search field action-button
   */
  get action(): string {
    return this.getAttribute(attributes.ACTION) ?? '';
  }

  /**
   * @param {boolean | string} value - allows multiple categories to be selected
   */
  set multiple(value: boolean | string) {
    if (value) {
      this.setAttribute(attributes.MULTIPLE, '');
    } else {
      this.removeAttribute(attributes.MULTIPLE);
    }
  }

  /**
   * @returns {boolean | string} determines whether multiple categories to be selected or not
   */
  get multiple(): boolean | string {
    return this.hasAttribute(attributes.MULTIPLE);
  }

  /**
   * Programmatically sets the search field's value and performs an optional search function
   * @param {any} val the incoming value to search for
   * @returns {Array<any>} containing search results, if applicable
   */
  async search(val: any): Promise<any> {
    let ret: any = [];
    // Note: Input is already sanitized by onBeforeInput for user input,
    // but we sanitize again for defense-in-depth and programmatic calls
    const safeVal: any = stripHTML(val);
    if (this.#previousSearchValue !== safeVal) {
      if (this.input) this.input.value = safeVal;
      this.#previousSearchValue = safeVal;

      if (typeof this.onSearch === 'function') {
        ret = await this.onSearch(safeVal);
      }
    }
    return ret;
  }

  /**
   * @param {string} val the new value to set
   */
  set value(val: string) {
    // Sanitize programmatically-set values for defense-in-depth
    const safeVal = stripHTML(val);
    super.value = safeVal;

    this.#previousSearchValue = val;

    if (typeof this.onSearch === 'function') {
      this.onSearch(safeVal); // Pass stripped value to callback
    }
  }

  get value(): string {
    return super.value;
  }

  /**
   * Define this method to carry out search functionality
   * (override this method when implementing an IdsSearchField).
   * @param {any} [val] the value to be searched for
   * @returns {Array<any>} containing matching search results
   */
  onSearch(val: any = undefined): any {
    return [`${stripHTML(val)}`];
  }

  /**
   * Hook called before processing input events from IdsFormInputMixin.
   * Sanitizes HTML from input value before events are emitted.
   * @returns {boolean} Always returns false to allow default input handling
   */
  onBeforeInput(): boolean {
    // Sanitize the input value synchronously before the mixin processes it
    if (this.input && this.input.value) {
      const sanitized = stripHTML(this.input.value);
      if (sanitized !== this.input.value) {
        // Set the sanitized value directly on the native input
        // This ensures the mixin reads the clean value
        this.input.value = sanitized;
      }
    }
    // Return false to allow normal input event processing
    return false;
  }

  /**
   * @param {any} value
   */
  #previousSearchValue: any = null;

  /**
   * Adds Search Field specific event handlers
   */
  #attachEventHandlers() {
    const handleSearchEvent = async (e: any) => {
      await this.search(e.target?.value);
    };

    this.offEvent('change', this.input);
    this.onEvent('change', this.input, handleSearchEvent);

    this.offEvent('input', this.input);
    this.onEvent('input', this.input, handleSearchEvent);

    this.offEvent('click', this.#categoriesActionButton);
    this.onEvent('click', this.#categoriesActionButton, async () => {
      await this.search(this.value);
      this.#triggerCategoriesEvent('search');
    });

    this.offEvent('selected', this.#categoriesPopup);
    this.onEvent('selected', this.#categoriesPopup, () => {
      this.#updateCategoriesMenu();
      this.#triggerCategoriesEvent('selected');
    });

    this.offEvent('deselected', this.#categoriesPopup);
    this.onEvent('deselected', this.#categoriesPopup, () => {
      this.#updateCategoriesMenu();
      this.#triggerCategoriesEvent('deselected');
    });

    this.#collapsibleResponsiveChange();
    this.onEvent('click', this.expandButton, (e: Event) => {
      e.stopPropagation();
      this.expandField();
    });

    // Truncated event handlers
    this.onEvent('click', this.truncatedWrapper, (e: Event) => {
      e.stopPropagation();
      e.preventDefault();
      this.expandTruncated();
    });

    this.offEvent('click', document);
    this.onEvent('click', document, (e: Event) => {
      if (this.state.expandedFromTruncated) {
        this.collapseTruncated(e);
      } else {
        this.collapseField(e);
      }
    });

    this.offEvent('click', this);
    this.onEvent('click', this, (e: Event) => {
      e.stopPropagation();
      this.collapsibleFocus();
    });

    this.offEvent('focusout', this);
    this.onEvent('focusout', this, (e: Event) => {
      if (this.state.expandedFromTruncated) {
        this.collapseTruncated(e);
      } else {
        this.collapseField(e);
      }
    });
  }

  /**
   * Listen for enter key to perform search function
   */
  #attachKeyboardListener() {
    this.offEvent('keydown', this.input);
    this.onEvent('keydown', this.input, (event: any) => {
      const shouldSearchOnReturn = !event?.path?.length || !event.path[0].classList || !event.path[0].classList.contains('ids-icon-button');
      if (['Enter'].indexOf(event.code) > -1 && shouldSearchOnReturn) {
        event.preventDefault();
      }

      switch (event.key) {
        case 'Enter':
          if (shouldSearchOnReturn) {
            this.onSearch(this.input?.value);
            this.#triggerCategoriesEvent('search');
          }
          this.expandField();
          break;
        default:
          break;
      }
    });
  }

  /**
   * Helper to trigger CustomEvent for various event-types
   * @param {'input' | 'search' | 'deselected' | 'selected'} eventType - the type of event to trigger
   */
  #triggerCategoriesEvent(eventType: 'input' | 'search' | 'deselected' | 'selected') {
    this.triggerEvent(eventType, this, {
      detail: {
        elem: this,
        categories: this.categories,
        categoriesSelected: this.selectedCategories,
        value: this.value,
      },
      bubbles: true,
      cancelable: false,
      composed: false
    });
  }

  #rerender() {
    if (this.container) {
      this.container.innerHTML = this.template();
      this.connectedCallback();
    }
  }

  /**
   * Updates popup alignment based on RTL direction
   * @private
   */
  #updatePopupAlignment(): void {
    const isRTL = this.localeAPI?.isRTL();
    const autocompletePopup = this.shadowRoot?.querySelector('ids-popup[part="popup"]');
    const categoriesPopup = this.#categoriesPopup;

    if (autocompletePopup) {
      autocompletePopup.setAttribute(attributes.ALIGN, isRTL ? 'bottom, right' : 'bottom, left');
    }

    if (categoriesPopup) {
      categoriesPopup.setAttribute(attributes.ALIGN, isRTL ? 'bottom, left' : 'bottom, right');
    }
  }
}
